// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Khipu 1.2 (owner, 2026-10-24; satellites ↔ catalog plan §4a): the
 * Satellites layer as a step keeps it. One place for the three jobs:
 *
 * - `captureKhipuSatellites`: the layer's state as a step's block (the solo
 *   recorder's scene, the editor's "Use the globe");
 * - `applyKhipuSatellites`: a step's block onto the layer (the player, on
 *   every surface: the layer is one object for the browser, the headset and
 *   the phone);
 * - `startSatelliteFollow` / `stopSatelliteFollow`: the browser globe keeps
 *   the selected satellite in view while a step with `follow` holds.
 *
 * The clock is kept as an offset from now in minutes (a Khipu plays on any
 * day), `live` when it follows the clock.
 */
import type { Map as MapLibreMap } from 'maplibre-gl'
import type { KhipuSatellites } from '../khipu'
import { SATELLITE_SPEEDS, SCRUB_LIMIT_MS } from './satelliteClock'
import { COLOUR_BYS, MAP_HEIGHTS, SHOW_SETS, type ColourBy, type MapHeight, type SatelliteLayer, type ShowSet } from './satelliteLayer'

/** The layer now, as a step would say it; `{ on: false }` when it is off. */
export function captureKhipuSatellites(layer: SatelliteLayer): KhipuSatellites {
  const s = layer.settings
  if (!s.on) return { on: false }
  const clock = layer.clock
  const offsetMinutes = Math.round(clock.offsetMs() / 60_000)
  return {
    on: true,
    show: [...s.show],
    colourBy: s.colourBy,
    selected: layer.selectedInfo?.norad ?? null,
    clock: {
      live: clock.live,
      ...(clock.live ? {} : { offsetMinutes }),
      speed: clock.speed,
      playing: clock.playing,
    },
    altitude: s.altitude,
    mapHeight: s.mapHeight,
    fov: s.fov,
    trails: s.trails,
  }
}

/** The fields a step may carry, read leniently: anything odd is left as it is. */
export function sanitiseKhipuSatellites(raw: unknown): KhipuSatellites | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  if (typeof o.on !== 'boolean') return null
  const out: KhipuSatellites = { on: o.on }
  if (Array.isArray(o.show)) {
    const show = o.show.filter((v): v is ShowSet => typeof v === 'string' && (SHOW_SETS as readonly string[]).includes(v))
    if (show.length) out.show = [...new Set(show)]
  }
  if (typeof o.colourBy === 'string' && (COLOUR_BYS as readonly string[]).includes(o.colourBy)) out.colourBy = o.colourBy as ColourBy
  if (o.selected === null) out.selected = null
  else if (Number.isInteger(o.selected) && (o.selected as number) > 0) out.selected = o.selected as number
  const c = o.clock && typeof o.clock === 'object' ? (o.clock as Record<string, unknown>) : null
  if (c) {
    const clock: NonNullable<KhipuSatellites['clock']> = {}
    if (typeof c.live === 'boolean') clock.live = c.live
    if (typeof c.offsetMinutes === 'number' && Number.isFinite(c.offsetMinutes)) clock.offsetMinutes = Math.max(-SCRUB_LIMIT_MS / 60_000, Math.min(SCRUB_LIMIT_MS / 60_000, Math.round(c.offsetMinutes)))
    if (typeof c.speed === 'number' && (SATELLITE_SPEEDS as readonly number[]).includes(c.speed)) clock.speed = c.speed
    if (typeof c.playing === 'boolean') clock.playing = c.playing
    if (Object.keys(clock).length) out.clock = clock
  }
  if (o.altitude === 'true' || o.altitude === 'compact') out.altitude = o.altitude
  if (typeof o.mapHeight === 'string' && (MAP_HEIGHTS as readonly string[]).includes(o.mapHeight)) out.mapHeight = o.mapHeight as MapHeight
  if (typeof o.fov === 'boolean') out.fov = o.fov
  if (typeof o.trails === 'boolean') out.trails = o.trails
  if (typeof o.follow === 'boolean') out.follow = o.follow
  if (typeof o.sky === 'boolean') out.sky = o.sky
  return out
}

/**
 * Puts a step's block on the layer. A satellite not yet loaded is selected
 * once the objects are in; one the mirror lacks stays unselected. `sky`
 * asks for Sky above me only where the session can show it.
 */
export function applyKhipuSatellites(layer: SatelliteLayer, s: KhipuSatellites): void {
  if (!s.on) {
    if (layer.onEarth && layer.on) layer.setOn(false)
    return
  }
  if (!layer.onEarth) return
  layer.update({
    on: true,
    ...(s.show?.length ? { show: s.show.filter((v): v is ShowSet => (SHOW_SETS as readonly string[]).includes(v)) } : {}),
    ...(s.colourBy ? { colourBy: s.colourBy } : {}),
    ...(s.altitude ? { altitude: s.altitude } : {}),
    ...(s.mapHeight ? { mapHeight: s.mapHeight } : {}),
    ...(typeof s.fov === 'boolean' ? { fov: s.fov } : {}),
    ...(typeof s.trails === 'boolean' ? { trails: s.trails } : {}),
  })
  const clock = s.clock
  if (clock) {
    if (clock.live) layer.clock.goLive()
    else if (typeof clock.offsetMinutes === 'number') layer.clock.seek(Date.now() + clock.offsetMinutes * 60_000)
    if (typeof clock.speed === 'number') layer.clock.setSpeed(clock.speed)
    if (typeof clock.playing === 'boolean') layer.clock.setPlaying(clock.playing)
  }
  if (s.selected === null) layer.select(null)
  else if (typeof s.selected === 'number') selectWhenLoaded(layer, s.selected)
  if (s.sky && layer.skyAvailable) layer.setSky(true)
}

function selectWhenLoaded(layer: SatelliteLayer, norad: number): void {
  if (layer.selectNorad(norad)) return
  const off = layer.onChange(() => {
    if (layer.status === 'ready' && layer.selectNorad(norad)) off()
    else if (layer.status === 'error' || !layer.on) off()
  })
}

let followTimer: ReturnType<typeof setInterval> | null = null

/**
 * The browser globe keeps the selected satellite under its centre while a
 * step with `follow` holds: a short ease to its sub-satellite point every
 * second (the layer moves it every frame; the camera need not). Stopped by
 * `stopSatelliteFollow` (the player leaving the step) or when nothing is
 * selected any more.
 */
export function startSatelliteFollow(layer: SatelliteLayer, getMap: () => MapLibreMap | null): void {
  stopSatelliteFollow()
  const tick = (): void => {
    const sel = layer.selected
    const map = getMap()
    if (sel === null || !layer.on || !map) {
      if (sel === null || !layer.on) stopSatelliteFollow()
      return
    }
    const p = layer.engine.current
    const x = p[sel * 3]
    if (x !== x) return
    const y = p[sel * 3 + 1]
    const z = p[sel * 3 + 2]
    const r = Math.hypot(x, y, z)
    const lon = (Math.atan2(y, x) * 180) / Math.PI
    const lat = (Math.asin(z / r) * 180) / Math.PI
    map.easeTo({ center: [lon, lat], duration: 900, essential: true })
  }
  tick()
  followTimer = setInterval(tick, 1000)
}

export function stopSatelliteFollow(): void {
  if (followTimer) clearInterval(followTimer)
  followTimer = null
}

export function satelliteFollowActive(): boolean {
  return followTimer !== null
}
